export function getStudentName(student) {
  return [student?.f_name, student?.l_name].filter(Boolean).join(' ').trim() || 'Unknown Student';
}

const ZONE_MAP = {
  Eastern: 'America/New_York',
  Central: 'America/Chicago',
  Mountain: 'America/Denver',
  Pacific: 'America/Los_Angeles',
  Alaska: 'America/Anchorage',
  Hawaii: 'Pacific/Honolulu',
};

export function getSchoolIanaTimeZone(timeZone, isDst) {
  if (timeZone === 'Mountain' && !isDst) return 'America/Phoenix';
  return ZONE_MAP[timeZone] || null;
}

export function formatContactName(contact) {
  return [contact?.f_name, contact?.l_name].filter(Boolean).join(' ').trim() || 'Unknown Contact';
}

export function getGradeLabel(grade, labelMap = {}, options = {}) {
  const { short = false } = options;

  if (grade === null || grade === undefined || grade === '') return short ? 'No grade' : 'No grade';

  const value = Number(grade);
  if (Number.isNaN(value)) return String(grade);
  if (labelMap[value]) return labelMap[value];
  if (value === 0) return 'Kindergarten';
  if (value === -1) return 'Pre-K';
  if (value === -2) return 'Preschool';
  if (value === -3) return 'Pre-Preschool';

  const remainderTen = value % 10;
  const remainderHundred = value % 100;
  let suffix = 'th';

  if (remainderTen === 1 && remainderHundred !== 11) suffix = 'st';
  if (remainderTen === 2 && remainderHundred !== 12) suffix = 'nd';
  if (remainderTen === 3 && remainderHundred !== 13) suffix = 'rd';

  return short ? `${value}${suffix}` : `${value}${suffix} Grade`;
}

export function formatTime(value, options = {}) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const timeZone = getSchoolIanaTimeZone(options.timeZone, options.isDst);

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
}

export function getPickupBadge(student, dismissalStatus, timeOptions = {}) {
  if (student?.status !== 'active') {
    return { label: student?.status || 'Inactive', tone: 'muted', rank: 5 };
  }

  if (['picked_up', 'completed'].includes(String(dismissalStatus?.status || '').toLowerCase())) {
    return {
      label: dismissalStatus.picked_at ? `Picked up at ${formatTime(dismissalStatus.picked_at, timeOptions)}` : 'Picked up',
      tone: 'success',
      rank: 3,
    };
  }

  if (dismissalStatus?.status === 'sent') {
    return {
      label: dismissalStatus.sent_at ? `Sent at ${formatTime(dismissalStatus.sent_at, timeOptions)}` : 'Sent to carpool',
      tone: 'warning',
      rank: 0,
    };
  }

  if (dismissalStatus?.status === 'called') {
    return {
      label: dismissalStatus.called_at ? `Called at ${formatTime(dismissalStatus.called_at, timeOptions)}` : 'Called for pickup',
      tone: 'warning',
      rank: 1,
    };
  }

  if (dismissalStatus?.status === 'waiting') {
    return {
      label: 'Waiting in queue',
      tone: 'info',
      rank: 2,
    };
  }

  return {
    label: 'Scheduled for pickup',
    tone: 'muted',
    rank: 4,
  };
}

export function parseChildrenIds(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map(Number).filter((item) => !Number.isNaN(item));
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parseChildrenIds(parsed);
    } catch {
      return value
        .split(',')
        .map((item) => Number(item.trim()))
        .filter((item) => !Number.isNaN(item));
    }
  }

  return [];
}

export const PERMISSION_LABELS = {
  'SUPER_ADMIN/SUPPORT': 'Super Admin',
  ADMIN: 'Admin',
  TEACHER: 'Teacher',
};

export function getPermissionLabel(permission) {
  return PERMISSION_LABELS[permission] || permission || 'Staff';
}

export function getStaffName(staff) {
  return [staff?.f_name, staff?.l_name].filter(Boolean).join(' ').trim() || 'Unknown Staff';
}

export function getStaffTitle(staff) {
  return staff?.position_title || getPermissionLabel(staff?.permission_level);
}

export function parseAssignedGrades(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return [...new Set(value.map(Number).filter((item) => !Number.isNaN(item)))].sort((left, right) => right - left);
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parseAssignedGrades(parsed);
    } catch {
      return [...new Set(
        value
          .split(',')
          .map((item) => Number(item.trim()))
          .filter((item) => !Number.isNaN(item))
      )].sort((left, right) => right - left);
    }
  }

  return [];
}

export function parseAssignedGradeSplitIds(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return [...new Set(
      value
        .map(Number)
        .filter((item) => Number.isInteger(item) && item > 0)
    )].sort((left, right) => left - right);
  }

  if (typeof value === 'string') {
    try {
      return parseAssignedGradeSplitIds(JSON.parse(value));
    } catch {
      return parseAssignedGradeSplitIds(value.split(',').map((item) => item.trim()));
    }
  }

  return [];
}
